import { useEffect, useState } from "react";
import { useT } from "../i18n/index.js";
import type { Settings as SettingsModel } from "../state/session.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { usePendingToggles } from "../ui/usePendingToggles.js";
import { Timer } from "../ui/Timer.js";
import { toast } from "../ui/Toast.js";
import { chatApi, socialApi } from "./api.js";
import { useSocial } from "./state.js";
import type { LedgerEntry } from "./types.js";

/** Settings additions of sub-project 6: blocked list, chat notifications, export, deletion, credit ledger (R74). */
export function SocialSettings({ settings, save }: { settings: SettingsModel; save: (patch: Partial<SettingsModel>) => Promise<void> }) {
  const t = useT();
  const chat = useSocial((s) => s.chat);
  const refreshChat = useSocial((s) => s.refreshChat);
  const logout = useSession((s) => s.logout);
  const toggles = usePendingToggles();
  const [deletion, setDeletion] = useState<{ requestedAt: number; dueAt: number } | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [exportUrl, setExportUrl] = useState<string | null>(null);

  useEffect(() => {
    void refreshChat();
    void socialApi.deletion().then(setDeletion, () => undefined);
    void socialApi.ledger("credits").then((r) => {
      setLedger(r.rows);
      setNext(r.next);
    }, () => undefined);
  }, [refreshChat]);

  const more = async () => {
    if (!next) return;
    const r = await socialApi.ledger("credits", next);
    setLedger((l) => [...l, ...r.rows]);
    setNext(r.next);
  };
  const exportData = async () => {
    const data = await socialApi.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    setExportUrl(URL.createObjectURL(blob));
  };
  const requestDeletion = async () => {
    if (!confirm(t("settings.delete.confirm"))) return;
    try {
      setDeletion(await socialApi.requestDeletion());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  };
  const cancelDeletion = async () => {
    await socialApi.cancelDeletion();
    setDeletion(null);
  };
  const notif = (key: string, label: string) => (
    <label className="row" style={{ marginBottom: 6 }}>
      <input type="checkbox" checked={toggles.shown(key, settings.notifications[key] ?? true)} onChange={(e) => void toggles.track(key, e.target.checked, () => save({ notifications: { ...settings.notifications, [key]: e.target.checked } })).catch((err: unknown) => toast.error(err instanceof Error ? err.message : String(err)))} data-testid={`notif-${key}`} />
      {label}
    </label>
  );

  return (
    <>
      <Panel title={t("settings.social")}>
        {notif("mention", t("settings.chat.mentions"))}
        {notif("mail", t("settings.chat.mail"))}
        <div className="hint" style={{ marginTop: 8 }}>{t("settings.blocked")}</div>
        {!chat || chat.blocked.length === 0 ? <p className="dim" style={{ margin: "4px 0" }}>{t("settings.blocked.none")}</p> : null}
        {chat?.blocked.map((b) => (
          <div key={b.id} className="row" style={{ padding: "4px 0" }} data-testid={`blocked-${b.id}`}>
            <span className="grow">{b.name}</span>
            <Button style={{ minHeight: 30 }} onClick={() => void chatApi.unblock(b.id).then(() => refreshChat())} data-testid="unblock">
              {t("social.profile.unblock")}
            </Button>
          </div>
        ))}
      </Panel>

      <Panel title={t("settings.ledger")}>
        <p className="hint" style={{ marginTop: 0 }}>{t("settings.ledger.hint")}</p>
        <div data-testid="credit-ledger">
          {ledger.map((e) => (
            <div key={e.id} className="row" style={{ padding: "4px 0", borderTop: "1px solid var(--line)", fontSize: 13 }}>
              <span className="mono" style={{ width: 70, color: e.delta >= 0 ? "var(--success)" : "var(--danger, #ef4444)" }}>
                {e.delta >= 0 ? "+" : ""}
                {e.delta}
              </span>
              <span className="grow">{e.reason}</span>
              <span className="mono dim">{e.after}</span>
              <span className="hint" style={{ marginLeft: 8 }}>{new Date(e.createdAt).toLocaleString()}</span>
            </div>
          ))}
        </div>
        {next ? (
          <Button style={{ marginTop: 8 }} onClick={() => void more()}>
            {t("settings.ledger.more")}
          </Button>
        ) : null}
      </Panel>

      <Panel title={t("settings.export")}>
        <p className="hint" style={{ marginTop: 0 }}>{t("settings.export.hint")}</p>
        <div className="row" style={{ gap: 8 }}>
          <Button onClick={() => void exportData()} data-testid="export-data">
            ⤓ {t("settings.export")}
          </Button>
          {exportUrl ? (
            <a className="btn" href={exportUrl} download="zend-account.json" data-testid="export-download">
              {t("settings.export.download")}
            </a>
          ) : null}
        </div>
      </Panel>

      <Panel title={t("settings.delete")}>
        <p className="hint" style={{ marginTop: 0 }}>{t("settings.delete.hint")}</p>
        {deletion ? (
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }} data-testid="deletion-pending">
            <span className="badge warn">
              {t("settings.delete.pending", { date: new Date(deletion.dueAt).toLocaleDateString() })} · <Timer finishesAt={deletion.dueAt} />
            </span>
            <Button onClick={() => void cancelDeletion()} data-testid="deletion-cancel">
              {t("settings.delete.cancel")}
            </Button>
            <Button variant="danger" onClick={() => void logout(true)}>
              {t("auth.logoutAll")}
            </Button>
          </div>
        ) : (
          <Button variant="danger" onClick={() => void requestDeletion()} data-testid="deletion-request">
            {t("settings.delete.request")}
          </Button>
        )}
      </Panel>
    </>
  );
}
